import React from "react";
import { Link } from "react-router-dom";
import { CreditCard, Calendar, RefreshCw, Sparkles, Mail } from "lucide-react";
import { useAppState } from "../hooks/useAppState";
import { useSubscription } from "../hooks/useSubscription";
import TopBar from "../components/layout/TopBar";
import SectionCard from "../components/sections/SectionCard";

// Nomi leggibili per i piani.
const PLAN_LABELS = {
  trial: "Trial",
  pro: "Pro",
  enterprise: "Enterprise",
  free: "Free",
};

// Converte un valore-data (Firestore Timestamp | Date | number | null) in millis.
function toMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  return null;
}

// Formatta la data di rinnovo in italiano, o "—" se assente.
function formatRenewalDate(currentPeriodEnd) {
  const ms = toMillis(currentPeriodEnd);
  if (ms == null) return "—";
  return new Date(ms).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function SubscriptionPage() {
  const { user, darkMode, toggleDarkMode, isSynced } = useAppState();
  const { plan, subscription, billingCycle, daysLeft, isTrialExpired, isPro, loading } =
    useSubscription();

  const planLabel = PLAN_LABELS[plan] || PLAN_LABELS.free;
  const renewalDate = formatRenewalDate(subscription?.currentPeriodEnd);
  const billingCycleLabel = billingCycle === "yearly" ? "Annuale" : "Mensile";
  const showTrialDaysLeft = plan === "trial" && !isTrialExpired && daysLeft != null;

  return (
    <div className={`min-h-screen transition-colors ${darkMode ? "bg-neutral-950 text-white" : "bg-neutral-50 text-neutral-900"}`}>
      <TopBar
        user={user}
        notificationsCount={0}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onUploadLogo={() => {}}
        isSynced={isSynced}
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-24">
        {/* Header */}
        <div className="mt-6 mb-6">
          <h1 className={`text-xl font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
            Abbonamento
          </h1>
          <p className={`mt-1 text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
            Gestisci il tuo piano e la fatturazione
          </p>
        </div>

        {loading ? (
          <div className={`rounded-xl border p-6 text-sm ${darkMode ? "bg-neutral-900 border-neutral-800 text-neutral-400" : "bg-white border-neutral-200 text-neutral-500"}`}>
            Caricamento abbonamento...
          </div>
        ) : (
          <div className="space-y-6">
            {/* Piano attuale */}
            <SectionCard
              title="Piano attuale"
              subtitle="Dettagli del tuo piano e della fatturazione"
              darkMode={darkMode}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${darkMode ? "bg-neutral-800" : "bg-forfy-50"}`}>
                  <CreditCard className={`w-5 h-5 ${darkMode ? "text-forfy-300" : "text-forfy-600"}`} />
                </div>
                <div>
                  <div className={`text-lg font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {planLabel}
                  </div>
                  {showTrialDaysLeft && (
                    <div className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                      {daysLeft === 1 ? "1 giorno rimanente" : `${daysLeft} giorni rimanenti`}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-2 text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    <Calendar className="w-4 h-4" />
                    Prossimo rinnovo
                  </div>
                  <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {renewalDate}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-2 text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    <RefreshCw className="w-4 h-4" />
                    Ciclo di fatturazione
                  </div>
                  <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {billingCycleLabel}
                  </div>
                </div>
              </div>

              {isPro ? (
                <p className={`mt-4 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                  Per modificare o disdire il tuo abbonamento contatta il supporto: richieste@forfy.it
                </p>
              ) : (
                <div className="mt-5">
                  <Link
                    to="/checkout"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-forfy-500 text-white text-sm font-medium hover:bg-forfy-600 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    Passa a Pro
                  </Link>
                </div>
              )}
            </SectionCard>

            {/* Contatto supporto per piani non Pro */}
            {!isPro && (
              <SectionCard title="Serve aiuto?" darkMode={darkMode}>
                <div className={`flex items-center gap-2 text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                  <Mail className="w-4 h-4 shrink-0" />
                  <span>
                    Per domande su piani e fatturazione scrivi a{" "}
                    <a href="mailto:richieste@forfy.it" className={`font-medium ${darkMode ? "text-forfy-300" : "text-forfy-600"} hover:underline`}>
                      richieste@forfy.it
                    </a>
                  </span>
                </div>
              </SectionCard>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
